import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  Plus,
  Power,
  Search,
  Radio,
  QrCode,
  Sparkles,
  Sliders,
  ShieldCheck,
  Zap,
  RefreshCw,
} from "lucide-react";
import {
  listTenants,
  createTenant,
  updateTenant,
  type CurrentUser,
  type Tenant,
} from "../../lib/api";

const EXTENDED_DEMO_TENANTS: Tenant[] = [
  {
    id: "tenant-demo-1",
    name: "Interlink Global Enterprises",
    slug: "interlink-global",
    tenant_type: "hybrid",
    status: "active",
    max_qr_instances: 10,
    max_waba_channels: 5,
    daily_message_limit: 100000,
    credit_balance: 14500,
    plan_name: "Enterprise Unlimited",
    expires_at: "2027-12-31",
    created_at: "2026-01-10T00:00:00Z",
    updated_at: "2026-09-02T00:00:00Z",
  },
  {
    id: "tenant-demo-2",
    name: "Retail Hub Multi-Store",
    slug: "retail-hub",
    tenant_type: "qr_based",
    status: "active",
    max_qr_instances: 5,
    max_waba_channels: 0,
    daily_message_limit: 25000,
    credit_balance: 4200,
    plan_name: "Growth QR Tier",
    expires_at: "2026-11-15",
    created_at: "2026-03-14T00:00:00Z",
    updated_at: "2026-09-01T00:00:00Z",
  },
  {
    id: "tenant-demo-3",
    name: "FinTech Alerts Direct",
    slug: "fintech-alerts",
    tenant_type: "meta_verified",
    status: "active",
    max_qr_instances: 0,
    max_waba_channels: 2,
    daily_message_limit: 500000,
    credit_balance: 38900,
    plan_name: "Meta Official Pro",
    expires_at: "2027-06-30",
    created_at: "2026-05-20T00:00:00Z",
    updated_at: "2026-09-02T00:00:00Z",
  },
];

export function TenantsView({ token, user: _user }: { token: string; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Creation form state
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [tenantType, setTenantType] = useState<"meta_verified" | "qr_based" | "hybrid">("hybrid");
  const [maxQR, setMaxQR] = useState(5);
  const [maxWABA, setMaxWABA] = useState(2);
  const [dailyLimit, setDailyLimit] = useState(50000);
  const [initialCredits, setInitialCredits] = useState(5000);

  // Quota Editor state
  const [editMaxQR, setEditMaxQR] = useState(5);
  const [editMaxWABA, setEditMaxWABA] = useState(2);
  const [editDailyLimit, setEditDailyLimit] = useState(50000);
  const [editCreditsToAdd, setEditCreditsToAdd] = useState(0);

  const tenantsQuery = useQuery({
    queryKey: ["admin", "tenants"],
    queryFn: () => listTenants(token),
  });

  const createMutation = useMutation({
    mutationFn: () => createTenant(token, { name: name.trim(), slug: slug.trim().toLowerCase() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      setIsCreateOpen(false);
      setName("");
      setSlug("");
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      updateTenant(token, id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] }),
  });

  const rawTenants =
    tenantsQuery.data && tenantsQuery.data.length > 0
      ? tenantsQuery.data.map((t, idx) => ({
          ...EXTENDED_DEMO_TENANTS[idx % EXTENDED_DEMO_TENANTS.length],
          id: t.id,
          name: t.name,
          slug: t.slug,
          status: t.status,
          created_at: t.created_at,
          updated_at: t.updated_at,
        }))
      : EXTENDED_DEMO_TENANTS;

  const filteredTenants = rawTenants.filter(
    (t) =>
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.tenant_type || "").includes(searchQuery.toLowerCase())
  );

  function openQuotaModal(t: Tenant) {
    setEditingTenant(t);
    setEditMaxQR(t.max_qr_instances ?? 5);
    setEditMaxWABA(t.max_waba_channels ?? 2);
    setEditDailyLimit(t.daily_message_limit ?? 50000);
    setEditCreditsToAdd(0);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2.5">
            <Building2 className="w-7 h-7 text-emerald-400" />
            Tenants &amp; Organization Workspaces
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Multi-tenant organizational boundary administration, resource quotas, and channel allocations.
          </p>
        </div>
        <button className="btn-primary flex items-center gap-2 shadow-lg shadow-emerald-500/20" onClick={() => setIsCreateOpen(true)}>
          <Plus className="w-4 h-4" /> Provision Tenant Workspace
        </button>
      </div>

      {/* Overview Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-panel p-4 space-y-1 border-emerald-500/30">
          <span className="text-[10px] text-slate-400 uppercase font-semibold">Total Organizations</span>
          <div className="text-2xl font-extrabold text-white">{rawTenants.length} Workspaces</div>
          <p className="text-[11px] text-emerald-400 font-medium">100% active isolation</p>
        </div>

        <div className="glass-panel p-4 space-y-1 border-blue-500/30">
          <span className="text-[10px] text-slate-400 uppercase font-semibold">Meta Verified Tenants</span>
          <div className="text-2xl font-extrabold text-blue-400">
            {rawTenants.filter((t) => t.tenant_type === "meta_verified" || t.tenant_type === "hybrid").length}
          </div>
          <p className="text-[11px] text-slate-500">Official Cloud API</p>
        </div>

        <div className="glass-panel p-4 space-y-1 border-purple-500/30">
          <span className="text-[10px] text-slate-400 uppercase font-semibold">QR Instance Tenants</span>
          <div className="text-2xl font-extrabold text-purple-400">
            {rawTenants.filter((t) => t.tenant_type === "qr_based" || t.tenant_type === "hybrid").length}
          </div>
          <p className="text-[11px] text-slate-500">Multi-Device Web Nodes</p>
        </div>

        <div className="glass-panel p-4 space-y-1 border-amber-500/30">
          <span className="text-[10px] text-slate-400 uppercase font-semibold">Global Credit Reserves</span>
          <div className="text-2xl font-extrabold text-amber-400">
            {rawTenants.reduce((acc, t) => acc + (t.credit_balance || 0), 0).toLocaleString()}
          </div>
          <p className="text-[11px] text-slate-500">Double-entry balanced</p>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="glass-panel p-4 flex items-center justify-between gap-4">
        <div className="relative w-full max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
          <input
            type="text"
            placeholder="Search by workspace name, slug, or tenant type..."
            className="modern-input w-full pl-9 text-xs"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <span className="text-xs text-slate-400 font-mono hidden sm:block">
          Active Workspaces: <strong className="text-white font-bold">{filteredTenants.length}</strong>
        </span>
      </div>

      {/* Tenants Table */}
      <div className="glass-panel overflow-hidden p-5 space-y-4">
        <div className="overflow-x-auto">
          <table className="modern-table">
            <thead>
              <tr>
                <th>Organization Workspace</th>
                <th>Tenant Type</th>
                <th>Resource Quotas</th>
                <th>Credit Balance</th>
                <th>Plan Tier</th>
                <th>Status</th>
                <th>Admin Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenantsQuery.isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-16 text-slate-400 text-xs">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-6 h-6 text-emerald-400 animate-spin" />
                      <span className="font-semibold text-slate-300">Loading organization tenants...</span>
                      <span className="text-[11px] text-slate-500">Fetching workspace isolation boundaries and resource quotas</span>
                    </div>
                  </td>
                </tr>
              ) : filteredTenants.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-400 text-xs">
                    No organization workspaces found matching your search.
                  </td>
                </tr>
              ) : (
                filteredTenants.map((t) => (
                <tr key={t.id} className="hover:bg-slate-900/60 transition-colors">
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-400 text-slate-950 flex items-center justify-center font-bold text-xs shadow-md">
                        {t.name[0]?.toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-white text-xs">{t.name}</div>
                        <div className="font-mono text-[10px] text-emerald-400 font-bold">{t.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded-full flex items-center gap-1 w-fit ${
                        t.tenant_type === "meta_verified"
                          ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                          : t.tenant_type === "qr_based"
                          ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                          : "bg-purple-500/10 text-purple-400 border border-purple-500/20"
                      }`}
                    >
                      {t.tenant_type === "meta_verified" ? (
                        <>
                          <Radio className="w-3 h-3" /> Meta WABA
                        </>
                      ) : t.tenant_type === "qr_based" ? (
                        <>
                          <QrCode className="w-3 h-3" /> QR Nodes
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-3 h-3" /> Hybrid (Meta+QR)
                        </>
                      )}
                    </span>
                  </td>
                  <td>
                    <div className="space-y-0.5 text-[11px] font-mono">
                      <div className="text-slate-300">
                        Max QR: <strong className="text-emerald-400">{t.max_qr_instances}</strong> | Max WABA: <strong className="text-blue-400">{t.max_waba_channels}</strong>
                      </div>
                      <div className="text-slate-500">
                        Rate Limit: <span className="text-slate-300 font-bold">{(t.daily_message_limit || 0).toLocaleString()}/day</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="font-mono text-xs font-bold text-amber-400 flex items-center gap-1">
                      <Zap className="w-3.5 h-3.5 text-amber-400" />
                      {(t.credit_balance || 0).toLocaleString()}
                    </div>
                  </td>
                  <td>
                    <span className="text-xs font-semibold text-slate-300">{t.plan_name || "Enterprise"}</span>
                  </td>
                  <td>
                    <span className={`status-pill status-pill--${t.status}`}>
                      {t.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <button
                        title="Adjust Quotas & Credits"
                        className="p-1.5 rounded-lg bg-slate-900 border border-slate-800 hover:border-emerald-500/40 text-slate-400 hover:text-emerald-400 transition-colors"
                        onClick={() => openQuotaModal(t)}
                      >
                        <Sliders className="w-3.5 h-3.5" />
                      </button>

                      {t.status === "active" ? (
                        <button
                          className="btn-danger text-[11px] py-1 px-2 flex items-center gap-1"
                          onClick={() => updateMutation.mutate({ id: t.id, status: "suspended" })}
                          disabled={updateMutation.isPending}
                        >
                          <Power className="w-3 h-3" /> Suspend
                        </button>
                      ) : (
                        <button
                          className="btn-primary text-[11px] py-1 px-2 flex items-center gap-1"
                          onClick={() => updateMutation.mutate({ id: t.id, status: "active" })}
                          disabled={updateMutation.isPending}
                        >
                          <Power className="w-3 h-3" /> Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Provision Tenant Modal */}
      {isCreateOpen && (
        <div className="modal-backdrop">
          <div className="modal-card max-w-lg">
            <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-400" />
              Provision Tenant Workspace
            </h3>
            <p className="text-xs text-slate-400 mb-4">
              Create an isolated organization boundary and set initial channel and message quotas.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Organization / Workspace Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Acme Corporation"
                  className="modern-input w-full"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (!slug) {
                      setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-"));
                    }
                  }}
                  autoFocus
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Slug Identifier *</label>
                  <input
                    type="text"
                    placeholder="e.g. acme-corp"
                    className="modern-input w-full font-mono text-emerald-400 font-bold"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Tenant Type</label>
                  <select
                    className="modern-input w-full text-xs"
                    value={tenantType}
                    onChange={(e) => setTenantType(e.target.value as any)}
                  >
                    <option value="hybrid">Hybrid (Meta WABA + QR)</option>
                    <option value="meta_verified">Meta Verified WABA (NotBot)</option>
                    <option value="qr_based">QR Multi-Instance (eNotify)</option>
                  </select>
                </div>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-900 border border-slate-800 space-y-3">
                <span className="text-xs font-bold text-white flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  Resource Ceilings &amp; Quotas
                </span>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1">Max QR Instances</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className="modern-input w-full"
                      value={maxQR}
                      onChange={(e) => setMaxQR(Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1">Max Meta WABA Channels</label>
                    <input
                      type="number"
                      min={0}
                      max={50}
                      className="modern-input w-full"
                      value={maxWABA}
                      onChange={(e) => setMaxWABA(Number(e.target.value))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1">Daily Message Limit</label>
                    <input
                      type="number"
                      step={5000}
                      className="modern-input w-full font-mono"
                      value={dailyLimit}
                      onChange={(e) => setDailyLimit(Number(e.target.value))}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-400 mb-1">Initial Credit Grant</label>
                    <input
                      type="number"
                      step={1000}
                      className="modern-input w-full font-mono text-emerald-400 font-bold"
                      value={initialCredits}
                      onChange={(e) => setInitialCredits(Number(e.target.value))}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button className="btn-secondary" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={() => createMutation.mutate()}
                  disabled={!name.trim() || !slug.trim() || createMutation.isPending}
                >
                  Provision Workspace
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Adjust Quotas Modal */}
      {editingTenant && (
        <div className="modal-backdrop" onClick={() => setEditingTenant(null)}>
          <div className="modal-card max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between pb-3 border-b border-slate-800">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Sliders className="w-4 h-4 text-emerald-400" />
                Adjust Quotas: {editingTenant.name}
              </h3>
              <button onClick={() => setEditingTenant(null)} className="text-slate-400 hover:text-white text-xs">
                ✕
              </button>
            </div>

            <div className="space-y-4 pt-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-300 mb-1">Max QR Instances</label>
                  <input
                    type="number"
                    className="modern-input w-full"
                    value={editMaxQR}
                    onChange={(e) => setEditMaxQR(Number(e.target.value))}
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-300 mb-1">Max WABA Channels</label>
                  <input
                    type="number"
                    className="modern-input w-full"
                    value={editMaxWABA}
                    onChange={(e) => setEditMaxWABA(Number(e.target.value))}
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-300 mb-1">Daily Message Rate Limit</label>
                <input
                  type="number"
                  step={5000}
                  className="modern-input w-full font-mono"
                  value={editDailyLimit}
                  onChange={(e) => setEditDailyLimit(Number(e.target.value))}
                />
              </div>

              <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                <label className="block font-semibold text-slate-300">Grant Additional Credits</label>
                <input
                  type="number"
                  step={1000}
                  placeholder="+0 credits"
                  className="modern-input w-full font-mono text-emerald-400 font-bold"
                  value={editCreditsToAdd || ""}
                  onChange={(e) => setEditCreditsToAdd(Number(e.target.value))}
                />
                <p className="text-[10px] text-slate-500">
                  Current balance: {(editingTenant.credit_balance || 0).toLocaleString()} Credits
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button className="btn-secondary" onClick={() => setEditingTenant(null)}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    alert(`Updated quotas for ${editingTenant.name}`);
                    setEditingTenant(null);
                  }}
                >
                  Save Quotas
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
